import { formatCents, type BidResult } from '@lbhl/shared';
import type { Tier } from '../lib/tiers.js';

export interface AdjustRow {
  id: string;
  label: string;
  dollars: string;
}

export function newAdjustRow(): AdjustRow {
  return { id: crypto.randomUUID(), label: '', dollars: '' };
}

interface Props {
  tiers: Tier[];
  rows: Record<string, AdjustRow[]>;
  result: BidResult | null;
  onChange(packageCode: string, rows: AdjustRow[]): void;
}

/**
 * Manual corrections, one package at a time.
 *
 * Scoped per tier because the customer is holding all three prices and has not
 * chosen: a flat $200 off means something very different on Essential than on
 * Showcase, so which price moved has to be recorded rather than inferred.
 *
 * A row with no reason or no amount is ignored rather than blocking the bid, and the
 * engine refuses a reason-less adjustment outright — an unexplained override would
 * undo the consistency this tool exists to provide.
 */
export function Adjustments({ tiers, rows, result, onChange }: Props) {
  return (
    <section className="card">
      <h2>Adjustments</h2>
      <div className="note">
        For what the rate card cannot express — a difficult roof, a referral discount,
        a goodwill credit. Use a minus sign to reduce a price. The customer still sees
        one all-in number per package; the reason is recorded for the office.
      </div>

      {tiers.map(({ pkg }) => {
        const mine = rows[pkg.code] ?? [];
        const net =
          result?.quotes.find((q) => q.packageCode === pkg.code)?.adjustmentTotalCents ?? 0;

        function update(next: AdjustRow[]) {
          onChange(pkg.code, next);
        }

        return (
          <div className="tier" key={pkg.code}>
            <h3>
              {pkg.name}
              {net !== 0 && <span className="desc">net {formatCents(net)}</span>}
            </h3>

            {mine.map((row, i) => (
              <div className="adjust" key={row.id}>
                <input
                  aria-label={`${pkg.name} adjustment ${i + 1} reason`}
                  placeholder="Reason (required)"
                  value={row.label}
                  onChange={(e) =>
                    update(mine.map((r) => (r.id === row.id ? { ...r, label: e.target.value } : r)))
                  }
                />
                <input
                  aria-label={`${pkg.name} adjustment ${i + 1} amount`}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={row.dollars}
                  onChange={(e) =>
                    update(
                      mine.map((r) => (r.id === row.id ? { ...r, dollars: e.target.value } : r)),
                    )
                  }
                />
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Remove ${pkg.name} adjustment ${i + 1}`}
                  onClick={() => update(mine.filter((r) => r.id !== row.id))}
                >
                  ✕
                </button>
              </div>
            ))}

            <button
              type="button"
              className="ghost"
              style={{ marginTop: mine.length > 0 ? 10 : 0 }}
              onClick={() => update([...mine, newAdjustRow()])}
            >
              + Adjust {pkg.name}
            </button>

            {mine.some((r) => r.dollars.trim() && !r.label.trim()) && (
              <div className="note warn">
                An adjustment needs a reason before it will be applied.
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
